import { useState, useEffect } from 'react';
import { 
  TrendingUp, Target, Lightbulb, Sparkles, Trophy,
  BookOpen, AlertCircle, CheckCircle, Flame, Star
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PerformanceInsights({ user }) {
  const [analysis, setAnalysis] = useState(null);
  const [motivation, setMotivation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalysis();
    fetchMotivation();
  }, [user]);

  const fetchAnalysis = async () => {
    try {
      const response = await axios.get(`${API}/user/performance-analysis/${user.user_id}`);
      setAnalysis(response.data);
    } catch (error) {
      console.error('Analysis error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMotivation = async () => {
    try {
      const response = await axios.get(`${API}/user/daily-motivation`);
      setMotivation(response.data);
    } catch (error) {
      console.error('Motivation error:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500"></div>
      </div>
    );
  }

  if (!analysis?.has_data) {
    return (
      <div className="space-y-6">
        {/* Motivational Welcome */}
        <div className="relative group">
          <div className="absolute -inset-1 bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500 rounded-2xl blur-lg opacity-50 group-hover:opacity-75 transition"></div>
          <div className="relative bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-8 text-center">
            <Sparkles className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
            <h2 className="text-3xl font-black text-white mb-3">
              Yolculuğunuza Hoş Geldiniz!
            </h2>
            <p className="text-white/80 text-lg mb-6">
              {motivation?.message || "🎯 Başarı, düzenli çalışma ile gelir. İlk adımı atın!"}
            </p>
            <p className="text-white/60">
              Henüz sınav geçmişiniz yok. Pratik yapmaya başlayarak performansınızı izleyebilir ve kişisel tavsiyelere sahip olabilirsiniz.
            </p>
          </div>
        </div>

        {/* Başarı Yol Haritası - Expanded */}
        <div className="space-y-6">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-black text-white mb-3">🎯 Başarıya Giden 7 Adım</h2>
            <p className="text-white/70 text-lg">Sınav başarınız için kanıtlanmış yol haritası</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Step 1 */}
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl blur-lg opacity-50 group-hover:opacity-75 transition"></div>
              <div className="relative bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-6 h-full">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white font-black text-xl">1</div>
                  <Target className="w-10 h-10 text-blue-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Hedef Belirleyin</h3>
                <ul className="space-y-2 text-white/80">
                  <li>📊 Günlük 20-30 soru hedefle</li>
                  <li>📅 Haftada 5-6 gün düzenli çalış</li>
                  <li>🎯 2-3 hafta içinde tüm konuları bitir</li>
                  <li>⏰ Her gün aynı saatte çalış</li>
                </ul>
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl blur-lg opacity-50 group-hover:opacity-75 transition"></div>
              <div className="relative bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-6 h-full">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center text-white font-black text-xl">2</div>
                  <BookOpen className="w-10 h-10 text-green-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Konuları Öğrenin</h3>
                <ul className="space-y-2 text-white/80">
                  <li>📚 5 ana kategoriden başla</li>
                  <li>✅ Her konudan en az 50 soru çöz</li>
                  <li>🔄 Zayıf konuları tekrarla</li>
                  <li>📖 Açıklamaları dikkatlice oku</li>
                </ul>
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl blur-lg opacity-50 group-hover:opacity-75 transition"></div>
              <div className="relative bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-6 h-full">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center text-white font-black text-xl">3</div>
                  <Flame className="w-10 h-10 text-purple-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Pratik Yap</h3>
                <ul className="space-y-2 text-white/80">
                  <li>🔥 Günde 2-3 pratik testi çöz</li>
                  <li>⚡ Zamanlı modda kendini test et</li>
                  <li>📈 Her gün skorunu artır</li>
                  <li>🎮 En az %80 başarı hedefle</li>
                </ul>
              </div>
            </div>

            {/* Step 4 */}
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl blur-lg opacity-50 group-hover:opacity-75 transition"></div>
              <div className="relative bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-6 h-full">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center text-white font-black text-xl">4</div>
                  <AlertCircle className="w-10 h-10 text-orange-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Hataları Analiz Et</h3>
                <ul className="space-y-2 text-white/80">
                  <li>❌ Yanlış soruları işaretle</li>
                  <li>🔍 Neden yanlış yaptığını anla</li>
                  <li>📝 Notlar al ve tekrarla</li>
                  <li>💡 Benzer sorulara odaklan</li>
                </ul>
              </div>
            </div>

            {/* Step 5 */}
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-yellow-500 to-amber-500 rounded-xl blur-lg opacity-50 group-hover:opacity-75 transition"></div>
              <div className="relative bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-6 h-full">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center text-white font-black text-xl">5</div>
                  <Trophy className="w-10 h-10 text-yellow-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Sınav Simülasyonu</h3>
                <ul className="space-y-2 text-white/80">
                  <li>🎯 Gerçek sınav formatında test çöz</li>
                  <li>⏱️ 50 dakika süre koyarak pratik yap</li>
                  <li>📊 En az %95 başarı hedefle</li>
                  <li>🔄 Sınava kadar 5-10 mock test çöz</li>
                </ul>
              </div>
            </div>

            {/* Step 6 */}
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500 to-blue-500 rounded-xl blur-lg opacity-50 group-hover:opacity-75 transition"></div>
              <div className="relative bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-6 h-full">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-indigo-500 rounded-full flex items-center justify-center text-white font-black text-xl">6</div>
                  <CheckCircle className="w-10 h-10 text-indigo-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Son Hazırlık</h3>
                <ul className="space-y-2 text-white/80">
                  <li>📝 Zor soruları tekrar et</li>
                  <li>🧠 Trafik işaretlerini ezberle</li>
                  <li>💤 Sınavdan önce iyi uyu</li>
                  <li>😊 Kendine güven ve sakin ol</li>
                </ul>
              </div>
            </div>

            {/* Step 7 */}
            <div className="relative group md:col-span-2 lg:col-span-3">
              <div className="absolute -inset-1 bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 rounded-xl blur-lg opacity-75 group-hover:opacity-100 transition animate-pulse"></div>
              <div className="relative bg-gradient-to-r from-pink-500/20 via-purple-500/20 to-indigo-500/20 backdrop-blur-xl border border-white/20 rounded-xl p-8">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full flex items-center justify-center text-white font-black text-2xl">7</div>
                  <Sparkles className="w-12 h-12 text-yellow-400 animate-pulse" />
                  <h3 className="text-2xl font-black text-white">SINAV GÜNÜ - BAŞARI!</h3>
                </div>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="text-white/90">
                    <p className="font-bold mb-2">🎯 Sınav Stratejisi:</p>
                    <ul className="text-sm space-y-1">
                      <li>• Kolay sorulardan başla</li>
                      <li>• Emin olmadığın soruları işaretle</li>
                      <li>• Zamanını iyi kullan (50 dk / 70 soru)</li>
                    </ul>
                  </div>
                  <div className="text-white/90">
                    <p className="font-bold mb-2">💪 Motivasyon:</p>
                    <ul className="text-sm space-y-1">
                      <li>• 1000+ kişi başardı, sen de başaracaksın!</li>
                      <li>• İyi hazırlandın, kendine güven</li>
                      <li>• Sakin ol ve sorulara odaklan</li>
                    </ul>
                  </div>
                  <div className="text-white/90">
                    <p className="font-bold mb-2">🎉 Başarı İpuçları:</p>
                    <ul className="text-sm space-y-1">
                      <li>• 52/70 doğru yeterli (%74)</li>
                      <li>• Aceleci olma, dikkatlice oku</li>
                      <li>• İlk içgüdünü takip et</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Real Statistics */}
          <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 backdrop-blur-xl border border-green-500/30 rounded-2xl p-6">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-green-400" />
              📊 Gerçek İstatistikler
            </h3>
            <div className="grid md:grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-3xl font-black text-green-400">2,500</p>
                <p className="text-white/70 text-sm">Güncel Soru</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-black text-blue-400">1000+</p>
                <p className="text-white/70 text-sm">Başarılı Kullanıcı</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-black text-purple-400">%95</p>
                <p className="text-white/70 text-sm">Ortalama Başarı</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-black text-orange-400">2-3</p>
                <p className="text-white/70 text-sm">Hafta Süre</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Daily Motivation Banner */}
      {motivation && (
        <div className="relative group overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 opacity-90"></div>
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMiIgZmlsbD0iI2ZmZiIgZmlsbC1vcGFjaXR5PSIwLjEiLz48L3N2Zz4=')] opacity-20"></div>
          <div className="relative p-6 flex items-center gap-4">
            <Sparkles className="w-12 h-12 text-white animate-pulse" />
            <div className="flex-1">
              <h3 className="text-2xl font-black text-white mb-1">Bugünün Mesajı</h3>
              <p className="text-white/90 text-lg font-semibold">{motivation.message}</p>
            </div>
          </div>
        </div>
      )}

      {/* Performance Overview */}
      <div className="grid md:grid-cols-3 gap-6">
        <div className="relative group">
          <div className="absolute -inset-1 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl blur-lg opacity-50 group-hover:opacity-75 transition"></div>
          <div className="relative bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <Trophy className="w-10 h-10 text-yellow-400" />
              <span className="text-3xl font-black text-white">{analysis.overall_success_rate}%</span>
            </div>
            <h3 className="text-white font-semibold mb-1">Başarı Oranı</h3>
            <p className="text-white/60 text-sm">Toplam performansınız</p>
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <CheckCircle className="w-10 h-10 text-green-400" />
            <span className="text-3xl font-black text-white">{analysis.total_exams}</span>
          </div>
          <h3 className="text-white font-semibold mb-1">Tamamlanan Sınav</h3>
          <p className="text-white/60 text-sm">{analysis.total_questions_answered} soru çözüldü</p>
        </div>

        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <Star className="w-10 h-10 text-purple-400" />
            <span className="text-3xl font-black text-white">{analysis.total_correct}</span>
          </div>
          <h3 className="text-white font-semibold mb-1">Doğru Cevap</h3>
          <p className="text-white/60 text-sm">Başarılı yanıtlarınız</p>
        </div>
      </div>

      {/* Weak Categories */}
      {analysis.weak_categories && Object.keys(analysis.weak_categories).length > 0 && (
        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <AlertCircle className="w-6 h-6 text-orange-400" />
            <h3 className="text-xl font-bold text-white">Geliştirilmesi Gereken Konular</h3>
          </div>
          <div className="space-y-3">
            {Object.entries(analysis.weak_categories).map(([category, count]) => (
              <div key={category} className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                <span className="text-white font-semibold">{category}</span>
                <span className="text-red-400 font-bold">{count} hata</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommendations */}
      {analysis.recommendations && analysis.recommendations.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 mb-2">
            <Lightbulb className="w-6 h-6 text-yellow-400" />
            <h3 className="text-xl font-bold text-white">Sizin İçin Öneriler</h3>
          </div>
          
          {analysis.recommendations.map((rec, index) => (
            <div key={index} className="relative group">
              <div className={`absolute -inset-1 rounded-xl blur-lg opacity-50 group-hover:opacity-75 transition ${
                rec.type === 'exam_ready' ? 'bg-gradient-to-r from-green-500 to-emerald-500' :
                rec.type === 'trending_up' ? 'bg-gradient-to-r from-blue-500 to-cyan-500' :
                rec.type === 'focus_area' ? 'bg-gradient-to-r from-orange-500 to-red-500' :
                'bg-gradient-to-r from-purple-500 to-pink-500'
              }`}></div>
              <div className="relative bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  {rec.type === 'exam_ready' && <Trophy className="w-8 h-8 text-green-400 flex-shrink-0" />}
                  {rec.type === 'trending_up' && <TrendingUp className="w-8 h-8 text-blue-400 flex-shrink-0" />}
                  {rec.type === 'focus_area' && <Target className="w-8 h-8 text-orange-400 flex-shrink-0" />}
                  {rec.type === 'study_more' && <BookOpen className="w-8 h-8 text-purple-400 flex-shrink-0" />}
                  
                  <div className="flex-1">
                    <h4 className="text-lg font-bold text-white mb-2">{rec.title}</h4>
                    <p className="text-white/70">{rec.description}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Motivational Message */}
      <div className="relative group">
        <div className="absolute -inset-1 bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 rounded-2xl blur-lg opacity-50 group-hover:opacity-75 transition animate-pulse"></div>
        <div className="relative bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-8 text-center">
          <Flame className="w-16 h-16 text-orange-400 mx-auto mb-4 animate-bounce" />
          <p className="text-2xl font-black text-white">
            {analysis.motivational_message}
          </p>
        </div>
      </div>
    </div>
  );
}
